import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { keycloakSession } from "@/auth/keycloak-session";

export type NotificationTone = "success" | "info" | "warning" | "error";
export type NotificationScope = "session" | "application";
export type NotificationPauseReason = "manual" | "hover" | "focus" | "action";

export interface NotificationAction {
  label: string;
  run: () => void | Promise<void>;
}

export interface NotificationInput {
  title: string;
  tone?: NotificationTone;
  message?: string;
  durationMs?: number | null;
  busy?: boolean;
  dedupeKey?: string;
  action?: NotificationAction;
  scope?: NotificationScope;
}

export interface NotificationUpdate {
  title?: string;
  tone?: NotificationTone;
  message?: string | null;
  durationMs?: number | null;
  busy?: boolean;
  dedupeKey?: string | null;
  action?: NotificationAction | null;
  scope?: NotificationScope;
}

export type NotificationPresetOptions = Omit<NotificationInput, "title" | "tone">;

export interface AppNotification {
  id: string;
  tone: NotificationTone;
  title: string;
  message: string | null;
  durationMs: number | null;
  busy: boolean;
  dedupeKey: string | null;
  actionLabel: string | null;
  actionPending: boolean;
  scope: NotificationScope;
  createdAt: number;
  updatedAt: number;
}

interface PreparedNotification {
  tone: NotificationTone;
  title: string;
  message: string | null;
  durationMs: number | null;
  busy: boolean;
  dedupeKey: string | null;
  action: NotificationAction | null;
  scope: NotificationScope;
}

interface RunningTimer {
  handle: number;
  startedAt: number;
}

export const NOTIFICATION_VISIBLE_LIMIT = 4;
export const NOTIFICATION_QUEUE_LIMIT = 20;

const actionFailureMessage = "The action could not be completed. Please try again.";
const sessionExpiredDedupeKey = "authentication-session-expired";

const defaultDurationsMs: Record<NotificationTone, number | null> = {
  success: 5_000,
  info: 6_000,
  warning: 8_000,
  error: null,
};

let nextNotificationId = 0;

function normalizedText(value: string): string | null {
  const normalized = value.trim();
  return normalized.length === 0 ? null : normalized;
}

function normalizedDuration(
  requested: number | null | undefined,
  tone: NotificationTone,
  busy: boolean,
  actionable: boolean,
): number | null {
  if (requested === null) {
    return null;
  }
  if (requested !== undefined) {
    return Number.isFinite(requested) ? Math.max(0, Math.round(requested)) : null;
  }
  if (busy || actionable) {
    return null;
  }
  return defaultDurationsMs[tone];
}

function prepareNotification(input: NotificationInput): PreparedNotification {
  const tone = input.tone ?? "info";
  const title = normalizedText(input.title) ?? "Washbox update";
  const message = input.message === undefined ? null : normalizedText(input.message);
  const actionLabel = input.action === undefined ? null : normalizedText(input.action.label);
  const action = actionLabel === null || input.action === undefined ? null : input.action;
  const busy = input.busy ?? false;

  return {
    tone,
    title,
    message,
    durationMs: normalizedDuration(input.durationMs, tone, busy, action !== null),
    busy,
    dedupeKey: input.dedupeKey === undefined ? null : normalizedText(input.dedupeKey),
    action,
    scope: input.scope ?? "session",
  };
}

export const useNotificationsStore = defineStore("notifications", () => {
  const notifications = ref<AppNotification[]>([]);
  const timers = new Map<string, RunningTimer>();
  const remainingDurations = new Map<string, number>();
  const pauseReasons = new Map<string, Set<NotificationPauseReason>>();
  const actionHandlers = new Map<string, NotificationAction["run"]>();
  const notificationRevisions = new Map<string, number>();
  let sessionRevision = 0;

  const visibleNotifications = computed(() =>
    notifications.value.slice(0, NOTIFICATION_VISIBLE_LIMIT),
  );
  const queuedCount = computed(() =>
    Math.max(0, notifications.value.length - NOTIFICATION_VISIBLE_LIMIT),
  );

  keycloakSession.subscribe((authenticated, reason) => {
    if (authenticated) {
      const expiredNotice = notifications.value.find(
        ({ dedupeKey }) => dedupeKey === sessionExpiredDedupeKey,
      );
      if (expiredNotice !== undefined) {
        removeNotification(expiredNotice.id);
      }
      return;
    }

    sessionRevision += 1;
    clear("session");
    if (reason === "expired") {
      warning("Your session expired", {
        message: "Sign in again to continue securely.",
        dedupeKey: sessionExpiredDedupeKey,
        scope: "application",
        action: {
          label: "Sign in",
          run: () =>
            keycloakSession.login(
              `${window.location.pathname}${window.location.search}${window.location.hash}`,
            ),
        },
      });
    }
  });

  function findNotification(id: string): AppNotification | undefined {
    return notifications.value.find((notification) => notification.id === id);
  }

  function isPaused(id: string): boolean {
    return (pauseReasons.get(id)?.size ?? 0) > 0;
  }

  function removePauseReason(id: string, reason: NotificationPauseReason): void {
    const reasons = pauseReasons.get(id);
    if (reasons === undefined) {
      return;
    }
    reasons.delete(reason);
    if (reasons.size === 0) {
      pauseReasons.delete(id);
    }
  }

  function stopTimer(id: string, preserveRemaining: boolean): void {
    const timer = timers.get(id);
    if (timer === undefined) {
      return;
    }
    window.clearTimeout(timer.handle);
    timers.delete(id);

    if (preserveRemaining) {
      const previousRemaining = remainingDurations.get(id) ?? 0;
      remainingDurations.set(id, Math.max(0, previousRemaining - (Date.now() - timer.startedAt)));
    }
  }

  function startTimer(notification: AppNotification): void {
    if (
      notification.durationMs === null ||
      isPaused(notification.id) ||
      timers.has(notification.id)
    ) {
      return;
    }

    const remaining = remainingDurations.get(notification.id) ?? notification.durationMs;
    remainingDurations.set(notification.id, remaining);
    const handle = window.setTimeout(() => dismiss(notification.id), remaining);
    timers.set(notification.id, { handle, startedAt: Date.now() });
  }

  function syncTimers(): void {
    const visibleIds = new Set(visibleNotifications.value.map(({ id }) => id));
    for (const id of timers.keys()) {
      if (!visibleIds.has(id)) {
        stopTimer(id, true);
      }
    }
    for (const notification of visibleNotifications.value) {
      startTimer(notification);
    }
  }

  function removeNotification(id: string, synchronize = true): void {
    const index = notifications.value.findIndex((notification) => notification.id === id);
    if (index < 0) {
      return;
    }

    stopTimer(id, false);
    remainingDurations.delete(id);
    pauseReasons.delete(id);
    actionHandlers.delete(id);
    notificationRevisions.delete(id);
    notifications.value.splice(index, 1);
    if (synchronize) {
      syncTimers();
    }
  }

  function replaceNotification(
    current: AppNotification,
    prepared: PreparedNotification,
  ): AppNotification {
    stopTimer(current.id, false);
    remainingDurations.delete(current.id);
    removePauseReason(current.id, "action");
    actionHandlers.delete(current.id);
    notificationRevisions.set(current.id, (notificationRevisions.get(current.id) ?? 0) + 1);

    if (prepared.action !== null) {
      actionHandlers.set(current.id, prepared.action.run);
    }

    const replacement: AppNotification = {
      id: current.id,
      tone: prepared.tone,
      title: prepared.title,
      message: prepared.message,
      durationMs: prepared.durationMs,
      busy: prepared.busy,
      dedupeKey: prepared.dedupeKey,
      actionLabel: prepared.action === null ? null : prepared.action.label.trim(),
      actionPending: false,
      scope: prepared.scope,
      createdAt: current.createdAt,
      updatedAt: Date.now(),
    };

    if (replacement.durationMs !== null) {
      remainingDurations.set(replacement.id, replacement.durationMs);
    }
    return replacement;
  }

  function isProtectedFromQueueOverflow(notification: AppNotification): boolean {
    return (
      notification.durationMs === null ||
      notification.tone === "error" ||
      notification.busy ||
      notification.actionLabel !== null ||
      notification.actionPending
    );
  }

  function enforceQueueLimit(): void {
    while (notifications.value.length > NOTIFICATION_QUEUE_LIMIT) {
      const oldestEvictable = notifications.value.find(
        (notification) => !isProtectedFromQueueOverflow(notification),
      );
      if (oldestEvictable === undefined) {
        return;
      }
      removeNotification(oldestEvictable.id, false);
    }
  }

  function notify(input: NotificationInput): string {
    const prepared = prepareNotification(input);
    if (prepared.dedupeKey !== null) {
      const existingIndex = notifications.value.findIndex(
        ({ dedupeKey }) => dedupeKey === prepared.dedupeKey,
      );
      if (existingIndex >= 0) {
        const existing = notifications.value[existingIndex];
        if (existing !== undefined) {
          notifications.value.splice(existingIndex, 1, replaceNotification(existing, prepared));
          enforceQueueLimit();
          syncTimers();
          return existing.id;
        }
      }
    }

    nextNotificationId += 1;
    const id = `notification-${nextNotificationId}`;
    const now = Date.now();
    const notification: AppNotification = {
      id,
      tone: prepared.tone,
      title: prepared.title,
      message: prepared.message,
      durationMs: prepared.durationMs,
      busy: prepared.busy,
      dedupeKey: prepared.dedupeKey,
      actionLabel: prepared.action === null ? null : prepared.action.label.trim(),
      actionPending: false,
      scope: prepared.scope,
      createdAt: now,
      updatedAt: now,
    };

    if (prepared.action !== null) {
      actionHandlers.set(id, prepared.action.run);
    }
    if (notification.durationMs !== null) {
      remainingDurations.set(id, notification.durationMs);
    }
    notificationRevisions.set(id, 1);
    notifications.value.push(notification);

    enforceQueueLimit();
    syncTimers();
    return id;
  }

  function update(id: string, patch: NotificationUpdate): boolean {
    const index = notifications.value.findIndex((notification) => notification.id === id);
    const current = notifications.value[index];
    if (index < 0 || current === undefined) {
      return false;
    }

    const input: NotificationInput = {
      title: patch.title ?? current.title,
      tone: patch.tone ?? current.tone,
      busy: patch.busy ?? current.busy,
      scope: patch.scope ?? current.scope,
    };

    const message = patch.message === undefined ? current.message : patch.message;
    if (message !== null) {
      input.message = message;
    }

    const dedupeKey = patch.dedupeKey === undefined ? current.dedupeKey : patch.dedupeKey;
    if (dedupeKey !== null) {
      input.dedupeKey = dedupeKey;
    }

    const currentAction = actionHandlers.get(id);
    if (patch.action !== null) {
      if (patch.action !== undefined) {
        input.action = patch.action;
      } else if (current.actionLabel !== null && currentAction !== undefined) {
        input.action = { label: current.actionLabel, run: currentAction };
      }
    }

    const timingBehaviorChanged =
      patch.tone !== undefined || patch.busy !== undefined || patch.action !== undefined;
    if (patch.durationMs !== undefined) {
      input.durationMs = patch.durationMs;
    } else if (!timingBehaviorChanged) {
      input.durationMs = current.durationMs;
    }

    notifications.value.splice(index, 1, replaceNotification(current, prepareNotification(input)));
    enforceQueueLimit();
    syncTimers();
    return true;
  }

  function success(title: string, options: NotificationPresetOptions = {}): string {
    return notify({ ...options, title, tone: "success" });
  }

  function info(title: string, options: NotificationPresetOptions = {}): string {
    return notify({ ...options, title, tone: "info" });
  }

  function warning(title: string, options: NotificationPresetOptions = {}): string {
    return notify({ ...options, title, tone: "warning" });
  }

  function error(title: string, options: NotificationPresetOptions = {}): string {
    return notify({ ...options, title, tone: "error" });
  }

  function captureSession(): () => boolean {
    const capturedRevision = sessionRevision;
    return () => capturedRevision === sessionRevision;
  }

  function dismiss(id: string): void {
    removeNotification(id);
  }

  function clear(scope?: NotificationScope): void {
    const ids = notifications.value
      .filter((notification) => scope === undefined || notification.scope === scope)
      .map(({ id }) => id);
    for (const id of ids) {
      removeNotification(id, false);
    }
    syncTimers();
  }

  function pause(id: string, reason: NotificationPauseReason = "manual"): void {
    if (findNotification(id) === undefined) {
      return;
    }
    const reasons = pauseReasons.get(id) ?? new Set<NotificationPauseReason>();
    if (reasons.has(reason)) {
      return;
    }
    const wasPaused = reasons.size > 0;
    reasons.add(reason);
    pauseReasons.set(id, reasons);
    if (!wasPaused) {
      stopTimer(id, true);
    }
  }

  function resume(id: string, reason: NotificationPauseReason = "manual"): void {
    if (findNotification(id) === undefined) {
      return;
    }
    removePauseReason(id, reason);
    if (!isPaused(id)) {
      syncTimers();
    }
  }

  async function runAction(id: string): Promise<void> {
    const notification = findNotification(id);
    const action = actionHandlers.get(id);
    if (notification === undefined || action === undefined || notification.actionPending) {
      return;
    }

    const actionRevision = notificationRevisions.get(id);
    notification.actionPending = true;
    pause(id, "action");
    try {
      await action();
      if (notificationRevisions.get(id) === actionRevision) {
        dismiss(id);
      }
    } catch {
      if (notificationRevisions.get(id) === actionRevision) {
        update(id, {
          tone: "error",
          message: actionFailureMessage,
          durationMs: null,
          busy: false,
        });
      }
    }
  }

  return {
    notifications,
    visibleNotifications,
    queuedCount,
    notify,
    update,
    success,
    info,
    warning,
    error,
    captureSession,
    dismiss,
    clear,
    pause,
    resume,
    runAction,
  };
});
